import { NaturaType } from '../../invoice/entities';
import { VatRate } from '../../settings/entities';
import { ZPrinterManufacturer } from './z-printer-paper-manufacturer';
import { ZPrinterVatType } from './z-printer-vat-type';

// Mappatura NaturaType (comprese le sottonature) -> natura gestita dal registratore di cassa.
// Sui documenti commerciali si usa solo la natura principale (N1-N7), non le sottonature.
const NATURA_TYPE_TO_VAT_TYPE: Partial<Record<NaturaType, ZPrinterVatType>> = {
  [NaturaType.N1]: ZPrinterVatType.Ee,
  [NaturaType.N2_1]: ZPrinterVatType.Ns,
  [NaturaType.N2_2]: ZPrinterVatType.Ns,
  [NaturaType.N3_1]: ZPrinterVatType.Ni,
  [NaturaType.N3_2]: ZPrinterVatType.Ni,
  [NaturaType.N3_3]: ZPrinterVatType.Ni,
  [NaturaType.N3_4]: ZPrinterVatType.Ni,
  [NaturaType.N3_5]: ZPrinterVatType.Ni,
  [NaturaType.N3_6]: ZPrinterVatType.Ni,
  [NaturaType.N4]: ZPrinterVatType.Es,
  [NaturaType.N5]: ZPrinterVatType.Rm,
  [NaturaType.N6_1]: ZPrinterVatType.Al,
  [NaturaType.N6_2]: ZPrinterVatType.Al,
  [NaturaType.N6_3]: ZPrinterVatType.Al,
  [NaturaType.N6_4]: ZPrinterVatType.Al,
  [NaturaType.N6_5]: ZPrinterVatType.Al,
  [NaturaType.N6_6]: ZPrinterVatType.Al,
  [NaturaType.N6_7]: ZPrinterVatType.Al,
  [NaturaType.N6_8]: ZPrinterVatType.Al,
  [NaturaType.N6_9]: ZPrinterVatType.Al,
  [NaturaType.N7]: ZPrinterVatType.As,
};

export function mapNaturaTypeToZPrinterVatType(natura: NaturaType): ZPrinterVatType {
  return NATURA_TYPE_TO_VAT_TYPE[natura] ?? ZPrinterVatType.Al;
}

// Codice natura da dichiarare su una riga di documento (campo vat_nature). Derivato dal tipo registratore per riusare il collasso delle sottonature fatto sopra. Fonte: doc ZBridge "Aliquote e nature IVA".
const VAT_TYPE_TO_DOCUMENT_NATURA_CODE: Partial<Record<ZPrinterVatType, string>> = {
  [ZPrinterVatType.Ee]: 'N1',
  [ZPrinterVatType.Ns]: 'N2',
  [ZPrinterVatType.Ni]: 'N3',
  [ZPrinterVatType.Es]: 'N4',
  [ZPrinterVatType.Rm]: 'N5',
  [ZPrinterVatType.Al]: 'N6',
  [ZPrinterVatType.As]: 'N7',
};

export function resolveDocumentNaturaCode(natura: NaturaType): string | undefined {
  return VAT_TYPE_TO_DOCUMENT_NATURA_CODE[mapNaturaTypeToZPrinterVatType(natura)];
}

// Indice di casella fisso imposto dal firmware per ogni natura, su marche dove non è configurabile dall'utente (a differenza delle aliquote percentuali, che occupano uno slot libero).
//
// ZBridge "Aliquote e nature IVA". 'as' (N7, IVA assolta in altro stato UE) non ha una casella fissa su nessuna marca qui sotto.
//
// RCH NON è in questa tabella: su RCH gli indici sono liberi (0-39, scelti dall'utente), non caselle fisse
//
// Custom: aliquote sugli indici 1-5, le 6 nature sugli indici 6-11, ciascuna identificata dal suo codice natura (N1=1 ... N6=6), quindi indice = codice + 5
const CUSTOM_NATURA_INDEX: Partial<Record<ZPrinterVatType, number>> = {
  [ZPrinterVatType.Ee]: 6,
  [ZPrinterVatType.Ns]: 7,
  [ZPrinterVatType.Ni]: 8,
  [ZPrinterVatType.Es]: 9,
  [ZPrinterVatType.Rm]: 10,
  [ZPrinterVatType.Al]: 11,
};

const NATURA_INDEX_BY_MANUFACTURER: Partial<Record<ZPrinterManufacturer, Partial<Record<ZPrinterVatType, number>>>> = {
  [ZPrinterManufacturer.Epson]: {
    [ZPrinterVatType.Es]: 0,
    [ZPrinterVatType.Ee]: 10,
    [ZPrinterVatType.Ns]: 11,
    [ZPrinterVatType.Ni]: 12,
    [ZPrinterVatType.Rm]: 13,
    [ZPrinterVatType.Al]: 14,
  },
  [ZPrinterManufacturer.Custom]: CUSTOM_NATURA_INDEX,
  // ItalRetail è lo stesso hardware di Custom rimarchiato, quindi condivide la tabella.
  [ZPrinterManufacturer.ItalRetail]: CUSTOM_NATURA_INDEX,
};

export function resolveNaturaIndex(manufacturer: ZPrinterManufacturer, vatType: ZPrinterVatType): number | undefined {
  return NATURA_INDEX_BY_MANUFACTURER[manufacturer]?.[vatType];
}

// Nature che il protocollo RCH riconosce quando l'indice è libero (vedi sotto): manca 'as' (N7),
// per cui il protocollo RCH non ha un codice.
const RCH_SUPPORTED_NATURA_TYPES = new Set<ZPrinterVatType>([
  ZPrinterVatType.Es,
  ZPrinterVatType.Ee,
  ZPrinterVatType.Ns,
  ZPrinterVatType.Ni,
  ZPrinterVatType.Rm,
  ZPrinterVatType.Al,
]);

function resolveFreeIndexFromCashRegisterCode(vatRate: VatRate): number | undefined {
  const cashRegisterCode = parseInt(vatRate.cashRegisterCode ?? '');
  return Number.isNaN(cashRegisterCode) ? undefined : cashRegisterCode;
}

// Indice di casella che ci si aspetta per una data aliquota su una data marca:
// fonte unica di verità usata sia per costruire la fiscal-config da inviare (ZPrinterFiscalConfigVatIn.fromVatRate) sia per verificarla  in modo che le due parti non a posteriori leggendola dalla stampante (FeatureCashRegisterClosureService),
// possano più divergere come successo prima di questa mappatura condivisa.
export function resolveExpectedVatIndex(vatRate: VatRate, manufacturer: ZPrinterManufacturer): number | undefined {
  if (!vatRate.naturaTypeOnZeroVat) {
    return resolveFreeIndexFromCashRegisterCode(vatRate);
  }

  const vatType = mapNaturaTypeToZPrinterVatType(vatRate.naturaTypeOnZeroVat);

  // RCH: indice libero come per le aliquote percentuali, la natura è dichiarata a parte (campo `type`)
  if (manufacturer === ZPrinterManufacturer.Rch) {
    return RCH_SUPPORTED_NATURA_TYPES.has(vatType) ? resolveFreeIndexFromCashRegisterCode(vatRate) : undefined;
  }

  return resolveNaturaIndex(manufacturer, vatType);
}
